/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Marcus Biesel <m.biesel@techdivision.com>
 */

import './wdyr';
import React, { Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { queryClient } from './Services/API/QueryClient';
import './Providers/TranslationProvider';
import theme from './Config/ThemeConfig';
import 'dayjs/locale/de';
import './App.scss';
import { getQueryParam } from './Services/Helper/QueryParamsHelper';
import StoreAppFrame from './RootComponents/StoreAppFrame';
import BaseAppFrame from './RootComponents/BaseAppFrame';

/**
 * App Component
 * @returns {JSX.Element}
 * @constructor
 */
export function App() {
  /**
   * This function decides which AppFrame needs to be rendered.
   *
   * StoreAppFrame => This AppFrame stores every query parameter and given data-attribute in the session storage
   * BaseAppFrame => This AppFrame does not contain store logic
   *
   * @returns {Element}
   */
  const renderAppFrame = () => {
    const widgetName = getQueryParam('widgetName') || 'checkout';
    switch (widgetName) {
      case 'checkout':
        return <StoreAppFrame configKey={widgetName} />;
      default:
        return <BaseAppFrame configKey={widgetName} />;
    }
  };

  return (
    <Suspense>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <ThemeProvider theme={theme}>
            <CssBaseline />
            {renderAppFrame()}
          </ThemeProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </LocalizationProvider>
      </QueryClientProvider>
    </Suspense>
  );
}
